import React from "react";
import ct from "class-types.macro";
import Standard from "../Price";
import { User } from "~/common/Store/models/User";
import { SelectedProduct } from "~/common/Store/models/ProductListing";
import getLowestPrice from "../../getLowestPrice";
import applyPlusPricing from "../../applyPlusPricing";
import ExclusivePlusPricing from "./ExclusivePlusPricing";
import PlusMemberDiscount from "../PlusMemberDiscount";
import applyBelowMapPricing from "../applyBelowMapPricing";
import QuantityDiscounts from "../QuantityDiscounts";
import DivideByPricing from "../DivideByPricing";

interface Props {
  user: User;
  selectedProduct: SelectedProduct;
  productListingView?: string;
  setIsQuantityDiscountPopoverOpen?: React.Dispatch<
    React.SetStateAction<string>
  >;
}

const Registered = ({
  user,
  selectedProduct,
  productListingView,
  setIsQuantityDiscountPopoverOpen,
}: Props) => {
  const {
    price,
    activeSale,
    showBelowMinimumAdvertisedPriceForLoggedInCustomers,
    showBelowMinimumAdvertisedPriceForPlatinumAndPlusCustomers,
  } = selectedProduct;
  const { type } = price.minimumAdvertisedPriceProperties;
  const hasMAPStyle = !!type.trim();
  const hasMapStyleG = hasMAPStyle && type === "G";

  const applyBelowMapPrice = applyBelowMapPricing(
    {
      price,
      showBelowMinimumAdvertisedPriceForLoggedInCustomers,
      showBelowMinimumAdvertisedPriceForPlatinumAndPlusCustomers,
    },
    user,
  );

  const showPlusPricing = applyPlusPricing(
    price,
    activeSale,
    hasMAPStyle,
    !!applyBelowMapPrice,
  );

  const showExclusivePlusPricing = !!(
    user.hasWebPlus &&
    showBelowMinimumAdvertisedPriceForPlatinumAndPlusCustomers &&
    !hasMapStyleG
  );

  const getCustomText = () => {
    if (showPlusPricing || showExclusivePlusPricing || hasMapStyleG) {
      return "";
    }

    if (
      user.platinum &&
      user.platinum.enabled &&
      showBelowMinimumAdvertisedPriceForPlatinumAndPlusCustomers
    ) {
      return "Exclusive Platinum Pricing";
    }

    return " Custom Quote for ";
  };

  return (
    <div className="map map-override">
      {getCustomText().length > 0 && (
        <Label>
          <CustomText value={getCustomText()} user={user} />
        </Label>
      )}
      <PriceDisplay
        {...{
          selectedProduct,
          user,
          showPlusPricing,
          showExclusivePlusPricing,
          productListingView,
          setIsQuantityDiscountPopoverOpen,
        }}
      />
    </div>
  );
};

interface PriceDisplayProps {
  user: User;
  selectedProduct: SelectedProduct;
  showPlusPricing: boolean;
  showExclusivePlusPricing: boolean;
  productListingView?: string;
  setIsQuantityDiscountPopoverOpen?: React.Dispatch<
    React.SetStateAction<string>
  >;
}

function PriceDisplay({
  user,
  selectedProduct,
  showPlusPricing,
  showExclusivePlusPricing,
  productListingView,
  setIsQuantityDiscountPopoverOpen,
}: PriceDisplayProps) {
  const {
    price,
    activeSale,
    showBelowMinimumAdvertisedPriceForLoggedInCustomers,
    showBelowMinimumAdvertisedPriceForPlatinumAndPlusCustomers,
    unitOfMeasure,
  } = selectedProduct;

  const lowestPrice = () => {
    return getLowestPrice(
      price,
      user,
      activeSale,
      showBelowMinimumAdvertisedPriceForLoggedInCustomers,
      showBelowMinimumAdvertisedPriceForPlatinumAndPlusCustomers,
    );
  };

  if (selectedProduct && selectedProduct.price.hasQuantityDiscounts) {
    return (
      <QuantityDiscounts
        selectedProduct={selectedProduct}
        user={user}
        productListingView={productListingView}
        setIsQuantityDiscountPopoverOpen={setIsQuantityDiscountPopoverOpen}
      />
    );
  }

  if (showPlusPricing) {
    return (
      <PlusMemberDiscount
        productListingView={productListingView}
        selectedProduct={selectedProduct}
        user={user}
      />
    );
  }

  if (showExclusivePlusPricing) {
    return (
      <ExclusivePlusPricing
        mapPrice={price.minimumAdvertisedPriceProperties.price}
        p1Price={price.price}
        unitOfMeasure={unitOfMeasure}
        hasQuantityDiscounts={price.hasQuantityDiscounts}
        divideByUnit={selectedProduct.divideByUnit}
        divideQuantity={selectedProduct.divideQuantity}
      />
    );
  }

  return (
    <>
      <Standard
        price={lowestPrice()}
        unitOfMeasure={unitOfMeasure}
        productListingView={productListingView}
      />
      <DivideByPricing
        dataTestId="divideByPricing"
        price={lowestPrice()}
        divideByUnit={selectedProduct.divideByUnit}
        divideQuantity={selectedProduct.divideQuantity}
      />
    </>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return (
    <p
      className={`to-see-label ${ct(
        "block",
        "font-semibold",
        "text-xs",
        "leading-none",
        "mb-0",
        "text-center",
        "text-gray-800",
        "w-full",
      )}`}
    >
      {children}
    </p>
  );
}

function CustomText({ value, user }: { value: string; user: User }) {
  if (value === "Exclusive Platinum Pricing") {
    return <>Exclusive Platinum Pricing</>;
  }
  return (
    <>
      {" Custom Quote for "}
      <span className="capitalize">
        {user.companyName ? user.companyName : user.fullName}
      </span>
    </>
  );
}

export default Registered;
